import type { Query, QueryKey } from '@tanstack/react-query';
import { createSyncStoragePersister } from '@tanstack/query-sync-storage-persister';
import { removeOldestQuery, type Persister } from '@tanstack/react-query-persist-client';

export const CRM_QUERY_CACHE_MAX_AGE_MS = 60 * 60 * 1000;
export const CRM_QUERY_CACHE_BUSTER = 'experviser-crm-query-cache-v1';
const CRM_QUERY_CACHE_PREFIX = 'experviser:query-cache:';

// La cache persistita e' volutamente limitata ai read-model operativi. Corpi
// email, file, debug, configurazioni e credenziali restano esclusi.
const PERSISTED_API_PREFIXES = [
  '/api/exai/rooms/focus',
  '/api/dashboard/',
  '/api/activities',
  '/api/contacts',
  '/api/companies',
  '/api/deals',
  '/api/opportunities',
  '/api/leads',
  '/api/meetings',
  '/api/calendar',
  '/api/branches',
  '/api/brands',
  '/api/synergies',
  '/api/tasks',
] as const;

const PERSISTED_CRM_QUERY_ENTITIES = new Set([
  'company', 'contact', 'branch', 'lead', 'deal', 'synergy', 'task', 'meeting', 'brand',
]);

function readAuthToken(): string {
  if (typeof window === 'undefined') return '';
  try {
    return window.localStorage.getItem('auth_token') ?? '';
  } catch {
    return '';
  }
}

// Il token non viene salvato nella chiave. Questo fingerprint serve soltanto
// a separare due sessioni nello stesso tab; il token e' firmato dal server e
// la cache vive in sessionStorage, quindi sparisce alla chiusura della scheda.
export function authSessionFingerprint(token: string): string {
  let hash = 0x811c9dc5;
  for (let index = 0; index < token.length; index += 1) {
    hash ^= token.charCodeAt(index);
    hash = Math.imul(hash, 0x01000193);
  }
  return (hash >>> 0).toString(36);
}

export function persistedCrmQueryCacheKey(token = readAuthToken()): string {
  return `${CRM_QUERY_CACHE_PREFIX}${token ? authSessionFingerprint(token) : 'anonymous'}`;
}

export function isPersistedCrmQueryKey(queryKey: QueryKey): boolean {
  const first = queryKey[0];
  if (first === '/api/auth/me') return true;
  if (first === 'crm-query' && queryKey[1] === 'execute') {
    const input = queryKey[2];
    const entityType = input && typeof input === 'object' ? (input as { entityType?: unknown }).entityType : null;
    return typeof entityType === 'string' && PERSISTED_CRM_QUERY_ENTITIES.has(entityType);
  }
  return typeof first === 'string' && PERSISTED_API_PREFIXES.some((prefix) => first.startsWith(prefix));
}

export function shouldPersistCrmQuery(query: Query): boolean {
  return query.state.status === 'success'
    && query.state.data !== undefined
    && isPersistedCrmQueryKey(query.queryKey);
}

export function createCrmQueryPersister(): Persister | null {
  if (typeof window === 'undefined') return null;
  try {
    return createSyncStoragePersister({
      storage: window.sessionStorage,
      key: persistedCrmQueryCacheKey(),
      throttleTime: 250,
      // Se il browser raggiunge il limite di sessionStorage, conserva i dati
      // piu' recenti invece di disattivare silenziosamente tutta la cache.
      retry: removeOldestQuery,
    });
  } catch {
    return null;
  }
}

export function removeCurrentCrmQueryCache(): void {
  if (typeof window === 'undefined') return;
  try {
    window.sessionStorage.removeItem(persistedCrmQueryCacheKey());
  } catch {
    // Logout e scadenza sessione devono proseguire anche senza storage.
  }
}
